import { getLocale } from '../../utils/pathHelper';

type TranslationKeys =
'adult'|
'child'|
'studentNum'|
'seniorNum'|
'personNum' |
'days'|
'voucherNum'|
'dateOfUse'|
'termOfStay' |
'startDate'|
'timeSchedule'|
'addToCart'|
'purchase'|
'pickupPlace'|
'childPriceIsAppliedToPersonUnderAgeOf'|
'pleaseSelect'|
'pleaseSelectNumberOfTicketsAndDateOfUse'|
'pleaseSelectValidNumberOfParticipants'|
'vouchersWillBeIssuedForDays'|
'vouchersWillBeAddedToCartForDays'|
'price'|
'passenger'|
'firstName'|
'lastName'|
'firstNamePlaceholder'|
'lastNamePlaceholder'|
'limit'|
'required'|
'valueMissingError'|
'patternMismatchError'|
'useStartDate' |
'numPassengers' |
'sex' |
'nationality' |
'birthDate' |
'male' |
'female'

type TranslationLocales = 'en'|'ja'|'zh-CN'|'zh-HK'|'zh-TW';

export type ITranslation = {
  [key in TranslationKeys]: ({}?: { [arg: string]: string|number }) => string;
}

const locales: {
  [locale in TranslationLocales]: ITranslation
} = {
  'en': {
    adult: () => ('Adult'),
    child: () => ('Child'),
    studentNum: () => ('Student'),
    seniorNum: () => ('Senior'),
    personNum: () => ('No. of people'),
    days: () => ('Day(s)'),
    voucherNum: () => ('Pass(es)'),
    dateOfUse: () => ('Date of use'),
    termOfStay: () => ('Date of entry / Departure date (Japan time)'),
    startDate: () => ('Start Date'),
    timeSchedule: () => ('Time'),
    addToCart: () => ('Add to cart'),
    purchase: () => ('Buy'),
    pickupPlace: () => ('Pickup Location'),
    childPriceIsAppliedToPersonUnderAgeOf: ({ age }) => (`Child price is valid for ${age} and under.`),
    pleaseSelect: () => ('Please select'),
    pleaseSelectNumberOfTicketsAndDateOfUse: () => ('Please select amount and date'),
    pleaseSelectValidNumberOfParticipants: (
      { max }: { max: number }) => (`The total number of people must not exceed ${max} people.`),
    vouchersWillBeIssuedForDays: ({
      numberOfDays, dateDuration, numberOfVouchers }: {
      numberOfDays: number, dateDuration: number, numberOfVouchers: number
    }) => {
      const dayUnit = numberOfDays === 1 ? 'day' : 'days';
      const durationUnit = dateDuration === 1 ? 'day' : 'days';
      const voucherUnit = numberOfVouchers === 1 ? 'sheet' : 'sheets';
      return `The voucher will be issued for ${numberOfDays} ${dayUnit}
        (${dateDuration} ${durationUnit} ticket, ${numberOfVouchers} ${voucherUnit}).`
    },
    vouchersWillBeAddedToCartForDays: ({
      numberOfDays, dateDuration, numberOfVouchers }: {
      numberOfDays: number, dateDuration: number, numberOfVouchers: number
    }) => {
      const dayUnit = numberOfDays === 1 ? 'day' : 'days';
      const durationUnit = dateDuration === 1 ? 'day' : 'days';
      const voucherUnit = numberOfVouchers === 1 ? 'sheet' : 'sheets';
      return `The voucher of ${numberOfDays} ${dayUnit}
        (${dateDuration} ${durationUnit} pass, ${numberOfVouchers} ${voucherUnit}) will be added to your cart.`
    },
    // JRW
    price: () => ('Price'),
    passenger: () => ('Passenger'),
    firstName: () => ('First name'),
    lastName: () => ('Last name'),
    firstNamePlaceholder: () => ('e.g. JOHN'),
    lastNamePlaceholder: () => ('e.g. SMITH'),
    limit: () => ('Max. 10 per order'),
    required: () => ('Required'),
    valueMissingError: () => ('Please fill in the required item'),
    patternMismatchError: () => ('Please use half-width English characters'),
    useStartDate: () => ('Start Date (JST)'),
    numPassengers: () => ('Number of Passengers'),
    sex: () => ('Gender (Passport)'),
    nationality: () => ('Nationality (Passport)'),
    birthDate: () => ('Date Of Birth'),
    female: () => ('Female'),
    male: () => ('Male')
  },
  'ja': {
    adult: () => ('大人'),
    child: () => ('子供'),
    studentNum: () => ('学生'),
    seniorNum: () => ('シニア'),
    personNum: () => ('人数'),
    days: () => ('日分'),
    voucherNum: () => ('枚'),
    dateOfUse: () => ('使用日'),
    termOfStay: () => ('入国日 / 出国日 (日本時間)'),
    startDate: () => ('利用開始日'),
    timeSchedule: () => ('利用時間'),
    addToCart: () => ('カートに追加'),
    purchase: () => ('購入する'),
    pickupPlace: () => ('チケットの受け取り場所'),
    childPriceIsAppliedToPersonUnderAgeOf: ({ age }) => (`子供料金の適応は${age}歳以下です。`),
    pleaseSelect: () => ('選択してください'),
    pleaseSelectNumberOfTicketsAndDateOfUse: () => ('枚数と日付を選択してください。'),
    pleaseSelectValidNumberOfParticipants: (
      { max }: { max: number }) => (`合計で${max}人以内になるように人数を選択してください。`),
    vouchersWillBeIssuedForDays: ({
      numberOfDays, dateDuration, numberOfVouchers }: {
      numberOfDays: number, dateDuration: number, numberOfVouchers: number
    }) => (`${numberOfDays}日分のバウチャー(${dateDuration}日券 x ${numberOfVouchers}枚)が発行されます。`),
    vouchersWillBeAddedToCartForDays: ({
      numberOfDays, dateDuration, numberOfVouchers }: {
      numberOfDays: number, dateDuration: number, numberOfVouchers: number
    }) => (`${numberOfDays}日分のバウチャー(${dateDuration}日券 x ${numberOfVouchers}枚)がカートに追加されます。`),
    // JRW
    price: () => ('料金'),
    passenger: () => ('搭乘客'),
    firstName: () => ('名（半角ローマ字）'),
    lastName: () => ('姓（半角ローマ字）'),
    firstNamePlaceholder: () => ('例：TARO'),
    lastNamePlaceholder: () => ('例：YAMADA'),
    limit: () => ('1注文あたり最大10枚まで'),
    required: () => ('必須'),
    valueMissingError: () => ('必須項目を入力してください'),
    patternMismatchError: () => ('半角ローマ字で入力してください'),
    useStartDate: () => ('利用開始日(日本時間)'),
    numPassengers: () => ('人数'),
    sex: () => ('パスポート上の性別'),
    nationality: () => ('パスポート上の国籍'),
    birthDate: () => ('生年月日'),
    female: () => ('女性'),
    male: () => ('男性')
  },
  'zh-CN': {
    adult: () => ('成人'),
    child: () => ('儿童'),
    studentNum: () => ('学生'),
    seniorNum: () => ('老年人'),
    personNum: () => ('人數'),
    days: () => ('天份'),
    voucherNum: () => ('张'),
    dateOfUse: () => ('使用日'),
    termOfStay: () => ('入境日 / 出境日 (日本時間)'),
    startDate: () => ('开始使用日'),
    timeSchedule: () => ('时段'),
    addToCart: () => ('加进购物车'),
    purchase: () => ('购买'),
    pickupPlace: () => ('取票地点'),
    childPriceIsAppliedToPersonUnderAgeOf: ({ age }) => (`适用儿童价格的年龄为${age}以下。`),
    pleaseSelect: () => ('请选择'),
    pleaseSelectNumberOfTicketsAndDateOfUse: () => ('请选择张数及日期。'),
    pleaseSelectValidNumberOfParticipants: (
      { max }: { max: number }) => (`请选择总和${max}位以内的人。`),
    vouchersWillBeIssuedForDays: ({
      numberOfDays, dateDuration, numberOfVouchers }: {
      numberOfDays: number, dateDuration: number, numberOfVouchers: number
    }) => (`将发行${numberOfDays}天份的兑换券（${dateDuration}日券 x ${numberOfVouchers}张）。`),
    vouchersWillBeAddedToCartForDays: ({
      numberOfDays, dateDuration, numberOfVouchers }: {
      numberOfDays: number, dateDuration: number, numberOfVouchers: number
    }) => (`${numberOfDays}天份的兑换券（${dateDuration}日券 x ${numberOfVouchers}張）已加进购物车`),
    // JRW
    price: () => ('费用'),
    passenger: () => ('乘客'),
    firstName: () => ('名（英文字母）'),
    lastName: () => ('姓（英文字母）'),
    firstNamePlaceholder: () => ('例：MEIMEI'),
    lastNamePlaceholder: () => ('例：WANG'),
    limit: () => ('最多可购买10张'),
    required: () => ('必填'),
    valueMissingError: () => ('必填项目不得为空'),
    patternMismatchError: () => ('请填写半角英文字母'),
    useStartDate: () => ('开始使用日（日本时间）'),
    numPassengers: () => ('人数'),
    sex: () => ('护照上的性别'),
    nationality: () => ('护照上的国籍'),
    birthDate: () => ('出生日期'),
    female: () => ('女性'),
    male: () => ('男性')
  },
  'zh-HK': {
    adult: () => ('成人'),
    child: () => ('小童'),
    studentNum: () => ('學生'),
    seniorNum: () => ('長者'),
    personNum: () => ('人數'),
    days: () => ('天份'),
    voucherNum: () => ('張'),
    dateOfUse: () => ('使用日'),
    termOfStay: () => ('入境日 / 出境日 (日本時間)'),
    startDate: () => ('開始使用日'),
    timeSchedule: () => ('時段'),
    addToCart: () => ('加入購物車'),
    purchase: () => ('購買'),
    pickupPlace: () => ('取票地點'),
    childPriceIsAppliedToPersonUnderAgeOf: ({ age }) => (`${age}歲以下按照兒童票價收費。`),
    pleaseSelect: () => ('請選擇'),
    pleaseSelectNumberOfTicketsAndDateOfUse: () => ('請選擇日期與張數。'),
    pleaseSelectValidNumberOfParticipants: (
      { max }: { max: number }) => (`請選擇合共${max}位以內的人數。`),
    vouchersWillBeIssuedForDays: ({
      numberOfDays, dateDuration, numberOfVouchers }: {
      numberOfDays: number, dateDuration: number, numberOfVouchers: number
    }) => (`發行${numberOfDays}日的兌換券(${dateDuration}日券 x ${numberOfVouchers}張)。`),
    vouchersWillBeAddedToCartForDays: ({
      numberOfDays, dateDuration, numberOfVouchers }: {
      numberOfDays: number, dateDuration: number, numberOfVouchers: number
    }) => (`${numberOfDays}日的兌換券(${dateDuration}日券 x ${numberOfVouchers}張)已加入購物車。`),
    // JRW
    price: () => ('費用'),
    passenger: () => ('乘客'),
    firstName: () => ('名（羅馬字）'),
    lastName: () => ('姓（羅馬字）'),
    firstNamePlaceholder: () => ('例：CHAN TAI'),
    lastNamePlaceholder: () => ('例：MAN'),
    limit: () => ('最多可購買10張'),
    required: () => ('必填'),
    valueMissingError: () => ('請輸入必填項目'),
    patternMismatchError: () => ('請輸入半形英文字母'),
    useStartDate: () => ('開始使用日（日本時間）'),
    numPassengers: () => ('人數'),
    sex: () => ('護照上的性別'),
    nationality: () => ('護照上的國藉'),
    birthDate: () => ('出生日期'),
    female: () => ('女性'),
    male: () => ('男性')
  },
  'zh-TW': {
    adult: () => ('成人'),
    child: () => ('兒童'),
    studentNum: () => ('學生'),
    seniorNum: () => ('年長者'),
    personNum: () => ('人數'),
    days: () => ('天份'),
    voucherNum: () => ('張'),
    dateOfUse: () => ('使用日'),
    termOfStay: () => ('入境日 / 出境日 (日本時間)'),
    startDate: () => ('開始使用日'),
    timeSchedule: () => ('時段'),
    addToCart: () => ('加進購物車'),
    purchase: () => ('購買'),
    pickupPlace: () => ('取票地點'),
    childPriceIsAppliedToPersonUnderAgeOf: ({ age }) => (`適用兒童價格的年齡為${age}以下。`),
    pleaseSelect: () => ('請選擇'),
    pleaseSelectNumberOfTicketsAndDateOfUse: () => ('請選擇張數及日期。'),
    pleaseSelectValidNumberOfParticipants: (
      { max }: { max: number }) => (`請選擇總和${max}位以內的人數。`),
    vouchersWillBeIssuedForDays: ({
      numberOfDays, dateDuration, numberOfVouchers }: {
      numberOfDays: number, dateDuration: number, numberOfVouchers: number
    }) => (`將發行${numberOfDays}天份的兌換券（${dateDuration}日券 x ${numberOfVouchers}張）。`),
    vouchersWillBeAddedToCartForDays: ({
      numberOfDays, dateDuration, numberOfVouchers }: {
      numberOfDays: number, dateDuration: number, numberOfVouchers: number
    }) => (`${numberOfDays}天份的兌換券（${dateDuration}日券 x ${numberOfVouchers}張）已加進購物車。`),
    // JRW
    price: () => ('費用'),
    passenger: () => ('乘客'),
    firstName: () => ('名（羅馬字）'),
    lastName: () => ('姓（羅馬字）'),
    firstNamePlaceholder: () => ('例：MEIMEI'),
    lastNamePlaceholder: () => ('例：WANG'),
    limit: () => ('最多可購買10張'),
    required: () => ('必填'),
    valueMissingError: () => ('請輸入必填項目'),
    patternMismatchError: () => ('請輸入半形羅馬字'),
    useStartDate: () => ('開始使用日（日本時間）'),
    numPassengers: () => ('人數'),
    sex: () => ('護照上的性別'),
    nationality: () => ('護照上的國藉'),
    birthDate: () => ('出生日期'),
    female: () => ('女性'),
    male: () => ('男性')
  },
};

const t: ITranslation = locales[getLocale() || 'en'];

export default t;
